/**
 * O1E (Juli 2026) – OPS Sales-Katalog: Produktanlagemaske für GENAU EIN
 * vollständiges neues Draft-Produkt (alle 28 Modellfelder, typtreu).
 *
 * Drei getrennte Phasen: Erfassung → vollständige read-only Prüfansicht →
 * ausdrückliche Speicherbestätigung (erst dann löst der PARENT genau einen
 * POST an den bestehenden O1D-Endpunkt aus). KEIN direkter Save im Formular,
 * KEIN optimistisches Produkt, KEINE erfundenen fachlichen Defaults, KEINE
 * automatische product_id-/order-/Stripe-Erzeugung, KEINE Stripe-API.
 *
 * Lokale Validierung SPIEGELT ausschließlich die realen Backendregeln
 * (sales_catalog_model.validate_product + O1D-Strict-Typen); das Backend
 * bleibt die endgültige Autorität.
 */
import { useState } from "react";

import { AlertTriangle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Exakt die 30 Felder des realen Produktmodells (O1D-Strict-Vertrag + O1H).
// O1H (Feb 2026): `kategorie` und `verkaufsart` sind bei NEUER Produktanlage
// verbindlich (deutsche Werte, keine internen Alternativen).
export const PRODUKT_FELDER = [
  "product_id", "product_type", "label", "short_description", "long_description",
  "pricing_model", "billing_intervals", "currency", "vat_rate", "vat_note",
  "base_fee_net_cents", "per_seat_net_cents", "one_time_net_cents", "per_seat_tiers",
  "min_price_net_cents", "max_price_net_cents", "badge", "order", "seat_min",
  "seat_max", "trial_days", "visible_on_landing", "visible_on_checkout", "active",
  "stripe_product_id", "stripe_price_ids", "entitlements", "metadata",
  "kategorie", "verkaufsart",
  // OPS-02, Betreiberfreigabe 21.10.: Pflichtfeld der Neuanlage (routes/ops.py).
  "delivery_type",
];

// Belegte Modellwerte (sales_catalog_model.py) — keine erfundenen Werte.
const PRODUCT_TYPES = ["subscription", "one_time", "custom"];
const PRICING_MODELS = ["flat", "per_seat", "tiered_graduated", "custom"];
const INTERVALS = ["monthly", "yearly", "one_time"];

// O1H — strikt deutsche Betreiberwerte (identisch zu ALL_KATEGORIEN /
// ALL_VERKAUFSARTEN in sales_catalog_model.py). Keine englischen Feldnamen,
// keine internen Alternativwerte, keine zusätzlichen Auswahlmöglichkeiten.
const KATEGORIEN = ["Hauptprodukt", "Zusatzprodukt", "Service"];
const VERKAUFSARTEN = ["Einmalkauf", "Abonnement"];

// OPS-02, Betreiberfreigabe 21.10.: exakt ALL_DELIVERY_TYPES (sales_catalog_model.py);
// Wert technisch, Beschriftung deutsch, kein Standardwert.
const DELIVERY_TYPES = [
  ["arbeitsschutzmappe_pdf", "Arbeitsschutzmappe (PDF)"],
  ["unterweisungsnachweis_pdf", "Unterweisungsnachweis (PDF)"],
  ["zip_bundle", "Mehrere PDFs als ZIP"],
  ["download", "Download (Dokument)"],
  ["lizenz", "Lizenz / Freischaltung"],
  ["api", "Über Schnittstelle (API)"],
  ["service", "Dienstleistung (ohne Auslieferung)"],
  ["reserved", "Reserviert (Platzhalter)"],
];
const DELIVERY_TYPE_WERTE = DELIVERY_TYPES.map(([wert]) => wert);

// Technisch neutrale Ausgangswerte (KEINE fachlichen Defaults): Strings leer,
// Zahlen leer, Booleans false (sichtbar unbestätigt), Listen leer, JSON "{}".
const LEERES_FORMULAR = {
  product_id: "",
  product_type: "",
  label: "",
  short_description: "",
  long_description: "",
  pricing_model: "",
  billing_intervals: [],
  currency: "",
  vat_rate: "",
  vat_note: "",
  base_fee_net_cents: "",
  per_seat_net_cents: "",
  one_time_net_cents: "",
  per_seat_tiers: [],
  min_price_net_cents: "",
  max_price_net_cents: "",
  badge: "",
  order: "",
  seat_min: "",
  seat_max: "",
  trial_days: "",
  visible_on_landing: false,
  visible_on_checkout: false,
  active: false,
  stripe_product_id: "",
  stripe_price_ids: [],
  entitlements: "{}",
  metadata: "{}",
  // O1H — Pflicht für neue Produkte, aber ohne fachlichen Default (bewusste
  // Betreiberentscheidung); leerer String bedeutet „noch nicht ausgewählt".
  kategorie: "",
  verkaufsart: "",
  delivery_type: "",
};

const istGanzzahl = (s) => /^-?\d+$/.test(String(s).trim());
const zahlOderNull = (s) => (String(s).trim() === "" ? null : parseInt(String(s).trim(), 10));

// Euro-Hilfsanzeige NUR zur Darstellung (Requestwert bleibt der Cent-Rohwert).
function euroHilfe(s) {
  if (!istGanzzahl(s) || parseInt(s, 10) < 0) return "";
  const cents = parseInt(s, 10);
  return `${cents} Cent = ${(cents / 100).toFixed(2).replace(".", ",")} EUR (netto)`;
}

function jsonObjektFehler(text, feld) {
  try {
    const v = JSON.parse(text);
    if (v === null || typeof v !== "object" || Array.isArray(v)) {
      return `${feld}: JSON-Objekt erwartet (kein Array, kein Skalar)`;
    }
    return null;
  } catch {
    return `${feld}: ungültiges JSON`;
  }
}

// Lokale Validierung — SPIEGELT exakt validate_product() + O1D-Typvertrag.
export function lokaleFehler(f) {
  const errs = [];
  if (!f.product_id.trim()) errs.push("product_id fehlt");
  if (!PRODUCT_TYPES.includes(f.product_type)) errs.push("product_type fehlt oder ungültig");
  if (!f.label.trim()) errs.push("label fehlt");
  if (!PRICING_MODELS.includes(f.pricing_model)) errs.push("pricing_model fehlt oder ungültig");
  if (!f.currency.trim()) errs.push("currency fehlt");
  if (!istGanzzahl(f.vat_rate) || parseInt(f.vat_rate, 10) < 0) errs.push("vat_rate ungültig (Ganzzahl >= 0)");
  if (!istGanzzahl(f.order)) errs.push("order ungültig (Ganzzahl erforderlich)");

  if (f.billing_intervals.length === 0) errs.push("billing_intervals fehlt");
  if (f.product_type === "one_time") {
    if (f.billing_intervals.length !== 1 || f.billing_intervals[0] !== "one_time") {
      errs.push("one_time darf nur das Intervall 'one_time' haben");
    }
    if (!istGanzzahl(f.one_time_net_cents) || parseInt(f.one_time_net_cents, 10) <= 0) {
      errs.push("one_time benötigt einen Einmalpreis (one_time_net_cents > 0)");
    }
  } else if (f.product_type === "subscription") {
    if (f.billing_intervals.some((iv) => iv === "one_time")) {
      errs.push("subscription darf nur 'monthly'/'yearly' haben");
    }
    if (String(f.base_fee_net_cents).trim() === "" && String(f.per_seat_net_cents).trim() === "") {
      errs.push("subscription benötigt einen Preis (base_fee_net_cents oder per_seat_net_cents)");
    }
  } else if (f.product_type === "custom") {
    if (f.billing_intervals.some((iv) => iv === "one_time")) {
      errs.push("custom-Intervalle müssen 'monthly'/'yearly' sein");
    }
  }

  for (const name of [
    "base_fee_net_cents", "per_seat_net_cents", "one_time_net_cents",
    "min_price_net_cents", "max_price_net_cents", "seat_min", "seat_max", "trial_days",
  ]) {
    const raw = String(f[name]).trim();
    if (raw !== "" && !istGanzzahl(raw)) errs.push(`${name} ungültig (Ganzzahl oder leer)`);
  }
  for (const name of [
    "base_fee_net_cents", "per_seat_net_cents", "one_time_net_cents",
    "min_price_net_cents", "max_price_net_cents", "seat_min", "seat_max",
  ]) {
    const v = String(f[name]).trim();
    if (v !== "" && istGanzzahl(v) && parseInt(v, 10) < 0) errs.push(`${name} ungültig (>= 0 erwartet)`);
  }
  const smin = zahlOderNull(f.seat_min);
  const smax = zahlOderNull(f.seat_max);
  if (smin !== null && smax !== null && smin > smax) errs.push("seat_min > seat_max");
  const pmin = zahlOderNull(f.min_price_net_cents);
  const pmax = zahlOderNull(f.max_price_net_cents);
  if (pmin !== null && pmax !== null && pmin > pmax) errs.push("min_price_net_cents > max_price_net_cents");

  if (f.pricing_model === "tiered_graduated" && f.per_seat_tiers.length === 0) {
    errs.push("tiered_graduated benötigt eine nicht-leere per_seat_tiers-Staffel");
  }
  let prev = 0;
  let offenGesehen = false;
  f.per_seat_tiers.forEach((t, i) => {
    if (offenGesehen) errs.push(`tier[${i}]: nach einer offenen Stufe (up_to leer) darf keine weitere folgen`);
    if (!istGanzzahl(t.unit_net_cents) || parseInt(t.unit_net_cents, 10) < 0) {
      errs.push(`tier[${i}]: unit_net_cents ungültig (>= 0 erwartet)`);
    }
    const upRaw = String(t.up_to).trim();
    if (upRaw === "") {
      offenGesehen = true;
    } else if (!istGanzzahl(upRaw) || parseInt(upRaw, 10) <= 0) {
      errs.push(`tier[${i}]: up_to muss > 0 oder leer sein`);
    } else if (parseInt(upRaw, 10) <= prev) {
      errs.push(`tier[${i}]: up_to nicht streng aufsteigend`);
    } else {
      prev = parseInt(upRaw, 10);
    }
  });

  f.stripe_price_ids.forEach((row, i) => {
    if (!String(row.key).trim()) errs.push(`stripe_price_ids[${i}]: Schlüssel fehlt`);
    if (!String(row.value).trim()) errs.push(`stripe_price_ids[${i}]: Wert fehlt`);
  });
  const keys = f.stripe_price_ids.map((r) => String(r.key).trim());
  if (new Set(keys).size !== keys.length) errs.push("stripe_price_ids: doppelter Schlüssel");

  const je = jsonObjektFehler(f.entitlements, "entitlements");
  if (je) errs.push(je);
  const jm = jsonObjektFehler(f.metadata, "metadata");
  if (jm) errs.push(jm);

  // O1H — Pflicht + Enum. Alt-Produkte bleiben auf Modellebene mit None
  // erlaubt; für NEU angelegte Produkte erzwingt das Backend die Werte
  // (OpsSalesCatalogNewProductBody). Das Frontend spiegelt genau dies:
  // fehlender/ungültiger Wert → lokale Fehlermeldung, kein POST.
  if (!KATEGORIEN.includes(f.kategorie)) {
    errs.push("kategorie fehlt oder ungültig (Hauptprodukt | Zusatzprodukt | Service)");
  }
  if (!VERKAUFSARTEN.includes(f.verkaufsart)) {
    errs.push("verkaufsart fehlt oder ungültig (Einmalkauf | Abonnement)");
  }
  // OPS-02, Betreiberfreigabe 21.10.: Pflicht + Enum wie kategorie/verkaufsart.
  if (!DELIVERY_TYPE_WERTE.includes(f.delivery_type)) {
    errs.push("delivery_type fehlt oder ungültig (Auslieferungsart auswählen)");
  }
  return errs;
}

// Request-Produkt: exakt die 28 Modellfelder, typtreu — KEINE UI-Hilfsfelder,
// KEINE Euro-Werte, KEINE Normalisierung der product_id.
export function bauProdukt(f) {
  return {
    product_id: f.product_id,
    product_type: f.product_type,
    label: f.label,
    short_description: f.short_description,
    long_description: f.long_description,
    pricing_model: f.pricing_model,
    billing_intervals: [...f.billing_intervals],
    currency: f.currency,
    vat_rate: parseInt(f.vat_rate, 10),
    vat_note: f.vat_note,
    base_fee_net_cents: zahlOderNull(f.base_fee_net_cents),
    per_seat_net_cents: zahlOderNull(f.per_seat_net_cents),
    one_time_net_cents: zahlOderNull(f.one_time_net_cents),
    per_seat_tiers: f.per_seat_tiers.map((t) => ({
      up_to: String(t.up_to).trim() === "" ? null : parseInt(String(t.up_to).trim(), 10),
      unit_net_cents: parseInt(String(t.unit_net_cents).trim(), 10),
    })),
    min_price_net_cents: zahlOderNull(f.min_price_net_cents),
    max_price_net_cents: zahlOderNull(f.max_price_net_cents),
    badge: f.badge === "" ? null : f.badge,
    order: parseInt(f.order, 10),
    seat_min: zahlOderNull(f.seat_min),
    seat_max: zahlOderNull(f.seat_max),
    trial_days: zahlOderNull(f.trial_days),
    visible_on_landing: f.visible_on_landing,
    visible_on_checkout: f.visible_on_checkout,
    active: f.active,
    stripe_product_id: f.stripe_product_id === "" ? null : f.stripe_product_id,
    stripe_price_ids: Object.fromEntries(
      f.stripe_price_ids.map((r) => [String(r.key).trim(), String(r.value).trim()])
    ),
    entitlements: JSON.parse(f.entitlements),
    metadata: JSON.parse(f.metadata),
    // O1H — deutsche Werte 1:1 übernehmen (keine Umkodierung, keine i18n).
    kategorie: f.kategorie,
    verkaufsart: f.verkaufsart,
    // OPS-02, Betreiberfreigabe 21.10.
    delivery_type: f.delivery_type,
  };
}

function Feld({ id, label, hinweis, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[11px] uppercase tracking-wide text-slate-500">
        {label}
      </label>
      {children}
      {hinweis ? <p className="text-[11px] text-slate-400">{hinweis}</p> : null}
    </div>
  );
}

function CentFeld({ id, label, value, onChange }) {
  return (
    <Feld id={id} label={label} hinweis={euroHilfe(value) || "Cent (Ganzzahl) oder leer"}>
      <Input id={id} data-testid={id} value={value} inputMode="numeric"
             onChange={(e) => onChange(e.target.value)} className="bg-white" />
    </Feld>
  );
}

function BoolFeld({ id, label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" data-testid={id} checked={checked}
             onChange={(e) => onChange(e.target.checked)} />
      {label}
      <span className="text-[11px] text-slate-400">({checked ? "true" : "false"})</span>
    </label>
  );
}

function ReviewZeile({ id, label, value }) {
  return (
    <div className="flex flex-col gap-0.5 py-1 border-b border-slate-100 last:border-b-0">
      <dt className="text-[11px] uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="text-sm text-slate-800 break-all font-mono whitespace-pre-wrap" data-testid={id}>
        {value}
      </dd>
    </div>
  );
}

export function ProduktAnlageMaske({
  versionId,
  contentHash,
  busy,
  err,
  errCode,
  unklar,
  onCancel,
  onSubmit,
  onReloadDraft,
  onCheckUnklar,
}) {
  const [f, setF] = useState(LEERES_FORMULAR);
  const [step, setStep] = useState("edit"); // "edit" | "review"
  const [fehler, setFehler] = useState([]);

  const set = (key) => (value) => setF((prev) => ({ ...prev, [key]: value }));
  const toggleInterval = (iv) => (checked) =>
    setF((prev) => ({
      ...prev,
      billing_intervals: checked
        ? [...prev.billing_intervals, iv]
        : prev.billing_intervals.filter((x) => x !== iv),
    }));

  const zurPruefung = () => {
    if (busy) return;
    const errs = lokaleFehler(f);
    setFehler(errs);
    if (errs.length === 0) setStep("review");
  };

  const produkt = fehler.length === 0 && step === "review" ? bauProdukt(f) : null;

  return (
    <div
      className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/40 px-4 py-4 space-y-4"
      data-testid="ops-sales-catalog-add-product-form"
    >
      <div>
        <p className="text-sm font-semibold text-slate-800">
          Produkt anlegen — Draft <span className="font-mono">{versionId}</span>
        </p>
        <p className="text-xs text-slate-500">
          Alle 28 Produktfelder sind vollständig zu erfassen. Es werden keine Werte
          automatisch vorbelegt. expected_content_hash:{" "}
          <span className="font-mono break-all" data-testid="o1e-add-content-hash">{contentHash}</span>
        </p>
      </div>

      {step === "edit" && (
        <div className="space-y-4" data-testid="o1e-add-edit-step">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Feld id="o1e-add-product-id" label="product_id"
                  hinweis="Bewusste Betreiberangabe — keine automatische Erzeugung oder Normalisierung.">
              <Input id="o1e-add-product-id" data-testid="o1e-add-product-id" value={f.product_id}
                     onChange={(e) => set("product_id")(e.target.value)} className="bg-white font-mono" />
            </Feld>
            <Feld id="o1e-add-product-type" label="product_type">
              <select id="o1e-add-product-type" data-testid="o1e-add-product-type" value={f.product_type}
                      onChange={(e) => set("product_type")(e.target.value)}
                      className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="">— bitte wählen —</option>
                {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Feld>
            <Feld id="o1e-add-label" label="label">
              <Input id="o1e-add-label" data-testid="o1e-add-label" value={f.label}
                     onChange={(e) => set("label")(e.target.value)} className="bg-white" />
            </Feld>
          </div>

          {/* O1H — Betriebsfelder: strikt deutsche Werte, Pflicht bei Anlage.
              Kein fachlicher Default — der Betreiber muss bewusst wählen. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="o1e-add-o1h-block">
            <Feld id="o1e-add-kategorie" label="kategorie"
                  hinweis="Hauptprodukt · Zusatzprodukt · Service — Betreiberentscheidung, keine Vorbelegung.">
              <select id="o1e-add-kategorie" data-testid="o1e-add-kategorie" value={f.kategorie}
                      onChange={(e) => set("kategorie")(e.target.value)}
                      className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="">— bitte wählen —</option>
                {KATEGORIEN.map((k) => (
                  <option key={k} value={k} data-testid={`o1e-add-kategorie-option-${k}`}>{k}</option>
                ))}
              </select>
            </Feld>
            <Feld id="o1e-add-verkaufsart" label="verkaufsart"
                  hinweis="Einmalkauf · Abonnement — Betreiberentscheidung, keine Vorbelegung.">
              <select id="o1e-add-verkaufsart" data-testid="o1e-add-verkaufsart" value={f.verkaufsart}
                      onChange={(e) => set("verkaufsart")(e.target.value)}
                      className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="">— bitte wählen —</option>
                {VERKAUFSARTEN.map((v) => (
                  <option key={v} value={v} data-testid={`o1e-add-verkaufsart-option-${v}`}>{v}</option>
                ))}
              </select>
            </Feld>
            {/* OPS-02, Betreiberfreigabe 21.10.: Auslieferungsart (Pflicht bei Neuanlage). */}
            <Feld id="o1e-add-delivery-type" label="delivery_type"
                  hinweis="Auslieferungsart — Betreiberentscheidung, keine Vorbelegung.">
              <select id="o1e-add-delivery-type" data-testid="o1e-add-delivery-type" value={f.delivery_type}
                      onChange={(e) => set("delivery_type")(e.target.value)}
                      className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="">— bitte wählen —</option>
                {DELIVERY_TYPES.map(([wert, beschriftung]) => (
                  <option key={wert} value={wert} data-testid={`o1e-add-delivery-type-option-${wert}`}>
                    {beschriftung}
                  </option>
                ))}
              </select>
            </Feld>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Feld id="o1e-add-short-description" label="short_description">
              <Textarea id="o1e-add-short-description" data-testid="o1e-add-short-description" rows={2}
                        value={f.short_description}
                        onChange={(e) => set("short_description")(e.target.value)} className="bg-white" />
            </Feld>
            <Feld id="o1e-add-long-description" label="long_description">
              <Textarea id="o1e-add-long-description" data-testid="o1e-add-long-description" rows={2}
                        value={f.long_description}
                        onChange={(e) => set("long_description")(e.target.value)} className="bg-white" />
            </Feld>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Feld id="o1e-add-pricing-model" label="pricing_model">
              <select id="o1e-add-pricing-model" data-testid="o1e-add-pricing-model" value={f.pricing_model}
                      onChange={(e) => set("pricing_model")(e.target.value)}
                      className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="">— bitte wählen —</option>
                {PRICING_MODELS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Feld>
            <Feld id="o1e-add-currency" label="currency" hinweis="Keine automatische Vorbelegung.">
              <Input id="o1e-add-currency" data-testid="o1e-add-currency" value={f.currency}
                     onChange={(e) => set("currency")(e.target.value)} className="bg-white font-mono" />
            </Feld>
            <Feld id="o1e-add-vat-rate" label="vat_rate" hinweis="Ganzzahl >= 0">
              <Input id="o1e-add-vat-rate" data-testid="o1e-add-vat-rate" value={f.vat_rate} inputMode="numeric"
                     onChange={(e) => set("vat_rate")(e.target.value)} className="bg-white" />
            </Feld>
            <Feld id="o1e-add-order" label="order"
                  hinweis="Betreiberangabe — keine automatische Vergabe.">
              <Input id="o1e-add-order" data-testid="o1e-add-order" value={f.order} inputMode="numeric"
                     onChange={(e) => set("order")(e.target.value)} className="bg-white" />
            </Feld>
          </div>

          <Feld id="o1e-add-vat-note" label="vat_note">
            <Input id="o1e-add-vat-note" data-testid="o1e-add-vat-note" value={f.vat_note}
                   onChange={(e) => set("vat_note")(e.target.value)} className="bg-white" />
          </Feld>

          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">billing_intervals</p>
            <div className="flex flex-wrap gap-4" data-testid="o1e-add-billing-intervals">
              {INTERVALS.map((iv) => (
                <label key={iv} className="flex items-center gap-1.5 text-sm text-slate-700">
                  <input type="checkbox" data-testid={`o1e-add-interval-${iv}`}
                         checked={f.billing_intervals.includes(iv)}
                         onChange={(e) => toggleInterval(iv)(e.target.checked)} />
                  {iv}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <CentFeld id="o1e-add-one-time-net-cents" label="one_time_net_cents"
                      value={f.one_time_net_cents} onChange={set("one_time_net_cents")} />
            <CentFeld id="o1e-add-base-fee-net-cents" label="base_fee_net_cents"
                      value={f.base_fee_net_cents} onChange={set("base_fee_net_cents")} />
            <CentFeld id="o1e-add-per-seat-net-cents" label="per_seat_net_cents"
                      value={f.per_seat_net_cents} onChange={set("per_seat_net_cents")} />
            <CentFeld id="o1e-add-min-price-net-cents" label="min_price_net_cents"
                      value={f.min_price_net_cents} onChange={set("min_price_net_cents")} />
            <CentFeld id="o1e-add-max-price-net-cents" label="max_price_net_cents"
                      value={f.max_price_net_cents} onChange={set("max_price_net_cents")} />
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
              per_seat_tiers (Staffel: up_to leer = offene letzte Stufe)
            </p>
            <div className="space-y-2" data-testid="o1e-add-per-seat-tiers">
              {f.per_seat_tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input data-testid={`o1e-add-tier-${i}-up-to`} value={t.up_to} placeholder="up_to"
                         inputMode="numeric" className="bg-white w-28"
                         onChange={(e) => set("per_seat_tiers")(
                           f.per_seat_tiers.map((row, j) => (j === i ? { ...row, up_to: e.target.value } : row))
                         )} />
                  <Input data-testid={`o1e-add-tier-${i}-unit-net-cents`} value={t.unit_net_cents}
                         placeholder="unit_net_cents" inputMode="numeric" className="bg-white w-40"
                         onChange={(e) => set("per_seat_tiers")(
                           f.per_seat_tiers.map((row, j) => (j === i ? { ...row, unit_net_cents: e.target.value } : row))
                         )} />
                  <Button data-testid={`o1e-add-tier-${i}-remove`} variant="outline" size="sm"
                          onClick={() => set("per_seat_tiers")(f.per_seat_tiers.filter((_, j) => j !== i))}>
                    Entfernen
                  </Button>
                </div>
              ))}
              <Button data-testid="o1e-add-tier-add" variant="outline" size="sm"
                      onClick={() => set("per_seat_tiers")([...f.per_seat_tiers, { up_to: "", unit_net_cents: "" }])}>
                Staffelstufe hinzufügen
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Feld id="o1e-add-seat-min" label="seat_min" hinweis="Ganzzahl oder leer">
              <Input id="o1e-add-seat-min" data-testid="o1e-add-seat-min" value={f.seat_min} inputMode="numeric"
                     onChange={(e) => set("seat_min")(e.target.value)} className="bg-white" />
            </Feld>
            <Feld id="o1e-add-seat-max" label="seat_max" hinweis="Ganzzahl oder leer">
              <Input id="o1e-add-seat-max" data-testid="o1e-add-seat-max" value={f.seat_max} inputMode="numeric"
                     onChange={(e) => set("seat_max")(e.target.value)} className="bg-white" />
            </Feld>
            <Feld id="o1e-add-trial-days" label="trial_days" hinweis="Ganzzahl oder leer">
              <Input id="o1e-add-trial-days" data-testid="o1e-add-trial-days" value={f.trial_days} inputMode="numeric"
                     onChange={(e) => set("trial_days")(e.target.value)} className="bg-white" />
            </Feld>
            <Feld id="o1e-add-badge" label="badge" hinweis="Leer = nicht hinterlegt (null)">
              <Input id="o1e-add-badge" data-testid="o1e-add-badge" value={f.badge}
                     onChange={(e) => set("badge")(e.target.value)} className="bg-white" />
            </Feld>
          </div>

          <div className="flex flex-wrap gap-6" data-testid="o1e-add-flags">
            <BoolFeld id="o1e-add-active" label="active" checked={f.active} onChange={set("active")} />
            <BoolFeld id="o1e-add-visible-on-landing" label="visible_on_landing"
                      checked={f.visible_on_landing} onChange={set("visible_on_landing")} />
            <BoolFeld id="o1e-add-visible-on-checkout" label="visible_on_checkout"
                      checked={f.visible_on_checkout} onChange={set("visible_on_checkout")} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Feld id="o1e-add-stripe-product-id" label="stripe_product_id"
                  hinweis="Manuelles Datenfeld — keine Stripe-API, leer = null.">
              <Input id="o1e-add-stripe-product-id" data-testid="o1e-add-stripe-product-id"
                     value={f.stripe_product_id}
                     onChange={(e) => set("stripe_product_id")(e.target.value)} className="bg-white font-mono" />
            </Feld>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                stripe_price_ids (manuelle Schlüssel/Wert-Paare)
              </p>
              <div className="space-y-2" data-testid="o1e-add-stripe-price-ids">
                {f.stripe_price_ids.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input data-testid={`o1e-add-price-id-${i}-key`} value={row.key} placeholder="Schlüssel"
                           className="bg-white w-32 font-mono"
                           onChange={(e) => set("stripe_price_ids")(
                             f.stripe_price_ids.map((r, j) => (j === i ? { ...r, key: e.target.value } : r))
                           )} />
                    <Input data-testid={`o1e-add-price-id-${i}-value`} value={row.value} placeholder="price_…"
                           className="bg-white font-mono"
                           onChange={(e) => set("stripe_price_ids")(
                             f.stripe_price_ids.map((r, j) => (j === i ? { ...r, value: e.target.value } : r))
                           )} />
                    <Button data-testid={`o1e-add-price-id-${i}-remove`} variant="outline" size="sm"
                            onClick={() => set("stripe_price_ids")(f.stripe_price_ids.filter((_, j) => j !== i))}>
                      Entfernen
                    </Button>
                  </div>
                ))}
                <Button data-testid="o1e-add-price-id-add" variant="outline" size="sm"
                        onClick={() => set("stripe_price_ids")([...f.stripe_price_ids, { key: "", value: "" }])}>
                  Zuordnung hinzufügen
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Feld id="o1e-add-entitlements" label="entitlements (JSON-Objekt)">
              <Textarea id="o1e-add-entitlements" data-testid="o1e-add-entitlements" rows={3}
                        value={f.entitlements}
                        onChange={(e) => set("entitlements")(e.target.value)} className="bg-white font-mono text-xs" />
            </Feld>
            <Feld id="o1e-add-metadata" label="metadata (JSON-Objekt)">
              <Textarea id="o1e-add-metadata" data-testid="o1e-add-metadata" rows={3}
                        value={f.metadata}
                        onChange={(e) => set("metadata")(e.target.value)} className="bg-white font-mono text-xs" />
            </Feld>
          </div>

          {fehler.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2"
                 data-testid="o1e-add-validation-errors">
              <p className="text-sm text-red-700 flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Eingaben unvollständig oder ungültig:
              </p>
              <ul className="list-disc pl-6 text-xs text-red-700 space-y-0.5">
                {fehler.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button data-testid="o1e-add-cancel" variant="outline" size="sm" onClick={onCancel} disabled={busy}>
              Abbrechen
            </Button>
            <Button data-testid="o1e-add-review-button" size="sm" onClick={zurPruefung} disabled={busy}>
              Produkt prüfen
            </Button>
          </div>
        </div>
      )}

      {step === "review" && produkt && (
        <div className="space-y-3" data-testid="o1e-add-review">
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-0.5"
            data-testid="o1e-add-review-warnhinweis"
          >
            <p>Das Produkt wird ausschließlich dem ausgewählten Draft hinzugefügt.</p>
            <p>Die veröffentlichte Version bleibt unverändert.</p>
            <p>Das Produkt wird dadurch nicht automatisch veröffentlicht.</p>
            <p>Das Produkt wird dadurch nicht automatisch im Checkout freigegeben.</p>
            <p>Es werden keine Stripe-Produkte oder Preise erzeugt.</p>
            <p>Nach Anlage bleibt ein separater Publish-Schritt erforderlich.</p>
          </div>
          <dl className="rounded-md border border-slate-200 bg-white px-3 py-2">
            <ReviewZeile id="o1e-add-review-version-id" label="Ziel-Draft version_id" value={versionId} />
            <ReviewZeile id="o1e-add-review-expected-hash" label="expected_content_hash" value={contentHash} />
            {PRODUKT_FELDER.map((feld) => {
              const v = produkt[feld];
              const anzeige =
                v === null ? "null" : typeof v === "object" ? JSON.stringify(v, null, 1) : String(v);
              return (
                <ReviewZeile key={feld} id={`o1e-add-review-${feld}`} label={feld} value={anzeige} />
              );
            })}
          </dl>
          <div className="flex items-center gap-2">
            <Button data-testid="o1e-add-back" variant="outline" size="sm"
                    onClick={() => !busy && setStep("edit")} disabled={busy}>
              Zurück zur Bearbeitung
            </Button>
            <Button data-testid="o1e-add-save" size="sm"
                    onClick={() => !busy && onSubmit(produkt)} disabled={busy}>
              Produkt anlegen
            </Button>
          </div>
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="o1e-add-busy">
          <Loader2 className="w-4 h-4 animate-spin" />
          Produkt wird angelegt …
        </div>
      )}

      {err && (
        <div
          className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2"
          data-testid="o1e-add-error"
        >
          <p className="text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {err}
          </p>
          {errCode === "catalog_draft_stale" && (
            <Button data-testid="o1e-add-reload" variant="outline" size="sm"
                    onClick={onReloadDraft} disabled={busy}>
              Aktuellen Draft neu laden
            </Button>
          )}
        </div>
      )}

      {unklar && (
        <div
          className="flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
          data-testid="o1e-add-unklar"
        >
          <p className="text-sm text-amber-900">
            Das Ergebnis des Speicherversuchs ist unklar (Netzwerk-/Serverfehler). Es wird
            kein zweiter Speicherversuch ausgelöst — bitte zuerst den Draft neu laden und
            prüfen, ob das Produkt vorhanden ist.
          </p>
          <Button data-testid="o1e-add-check-unklar" variant="outline" size="sm"
                  onClick={onCheckUnklar} disabled={busy}>
            Draft neu laden und prüfen
          </Button>
        </div>
      )}
    </div>
  );
}
